import { axiosInstance } from './api';

// VISITS-2 (#175): visit template admin API — /visits/ (plugins/visits).
// Weights, formula coefficients, and therefore group/overall percentages
// are allowed to be negative — the API and BE never clamp them, so the FE
// must not either.

export interface VisitParameterDto {
  id: number;
  name: string;
  order: number;
  weight_yes: number;
  weight_no: number;
}

export interface VisitParameterGroupDto {
  id: number;
  name: string;
  order: number;
  formula_coefficient: number;
  parameters: VisitParameterDto[];
}

export interface VisitTemplateSummaryDto {
  id: number;
  name: string;
  zone_count: number;
}

export interface VisitTemplateZoneRefDto {
  id: number;
  name: string;
}

export interface VisitTemplateDetailDto {
  id: number;
  name: string;
  zones: VisitTemplateZoneRefDto[];
  groups: VisitParameterGroupDto[];
}

export interface VisitsZoneSettingsDto {
  enabled: boolean;
  visits_chat_id: string | null;
  visits_thread_id: string | null;
}

export const getVisitsZoneSettings = (zoneId: string | number) =>
  axiosInstance.get<VisitsZoneSettingsDto>(`/visits/zones/${zoneId}/settings/`);

/** Every template attached to this zone (groups, in order, for column
 *  headers) — a zone can have any number, including zero. */
export const getZoneVisitTemplates = (zoneId: string | number) =>
  axiosInstance.get<{ results: VisitTemplateDetailDto[] }>(`/visits/zones/${zoneId}/template/`);

export const patchVisitsZoneSettings = (zoneId: string | number, enabled: boolean) =>
  axiosInstance.patch<VisitsZoneSettingsDto>(`/visits/zones/${zoneId}/settings/`, { enabled });

/** "Отправить команде" chat/topic override (Alexey 2026-08-27) — same
 *  pattern as checklists' ops_tasks_chat_id/thread_id. Either field may be
 *  null to clear it (falls back to the zone's default client chat). */
export const patchVisitsZoneChat = (zoneId: string | number, visitsChatId: string | null, visitsThreadId: string | null) =>
  axiosInstance.patch<VisitsZoneSettingsDto>(`/visits/zones/${zoneId}/settings/`, {
    visits_chat_id: visitsChatId,
    visits_thread_id: visitsThreadId,
  });

export const listVisitTemplates = () =>
  axiosInstance.get<{ results: VisitTemplateSummaryDto[] }>('/visits/templates/');

export const createVisitTemplate = (name: string) =>
  axiosInstance.post<VisitTemplateSummaryDto>('/visits/templates/', { name });

export const getVisitTemplate = (templateId: number) =>
  axiosInstance.get<VisitTemplateDetailDto>(`/visits/templates/${templateId}/`);

export const patchVisitTemplate = (
  templateId: number,
  data: Partial<{ name: string }>,
) => axiosInstance.patch<VisitTemplateDetailDto>(`/visits/templates/${templateId}/`, data);

export const deleteVisitTemplate = (templateId: number) =>
  axiosInstance.delete(`/visits/templates/${templateId}/`);

/** Additive — a zone can have any number of templates attached at once. */
export const attachVisitTemplateZone = (templateId: number, zoneId: number) =>
  axiosInstance.post<VisitTemplateDetailDto>(`/visits/templates/${templateId}/zones/`, { zone_id: zoneId });

export const detachVisitTemplateZone = (templateId: number, zoneId: number) =>
  axiosInstance.delete(`/visits/templates/${templateId}/zones/${zoneId}/`);

/** Attach every zone in a branch group to this template in one call. */
export const attachVisitTemplateBranchGroup = (templateId: number, branchGroupId: number) =>
  axiosInstance.post<VisitTemplateDetailDto>(`/visits/templates/${templateId}/branch-groups/${branchGroupId}/`);

export const createVisitGroup = (
  templateId: number,
  data: { name: string; order?: number; formula_coefficient?: number },
) => axiosInstance.post<VisitParameterGroupDto>(`/visits/templates/${templateId}/groups/`, data);

export const patchVisitGroup = (
  templateId: number,
  groupId: number,
  data: Partial<{ name: string; order: number; formula_coefficient: number }>,
) => axiosInstance.patch<VisitParameterGroupDto>(`/visits/templates/${templateId}/groups/${groupId}/`, data);

export const deleteVisitGroup = (templateId: number, groupId: number) =>
  axiosInstance.delete(`/visits/templates/${templateId}/groups/${groupId}/`);

export const createVisitParameter = (
  templateId: number,
  groupId: number,
  data: { name: string; order?: number; weight_yes?: number; weight_no?: number },
) => axiosInstance.post<VisitParameterDto>(`/visits/templates/${templateId}/groups/${groupId}/parameters/`, data);

export const patchVisitParameter = (
  templateId: number,
  groupId: number,
  paramId: number,
  data: Partial<{ name: string; order: number; weight_yes: number; weight_no: number }>,
) =>
  axiosInstance.patch<VisitParameterDto>(
    `/visits/templates/${templateId}/groups/${groupId}/parameters/${paramId}/`,
    data,
  );

export const deleteVisitParameter = (templateId: number, groupId: number, paramId: number) =>
  axiosInstance.delete(`/visits/templates/${templateId}/groups/${groupId}/parameters/${paramId}/`);

// VISITS-3 (#176): taking/reading actual visits — /visits/zones/<id>/visits/
// and /visits/visits/<id>/... (plugins/visits/api/instances.py). Same rule
// as above: percentages/totals can be negative, never clamp them in the FE.

export interface VisitAuthorDto {
  id: number;
  username: string;
  name: string;
}

/** group_id -> percentage (0-100, can go negative), or null if nothing in
 *  that group has been answered yet — render "—", not "0%". */
export type VisitGroupScoresDto = Record<number, number | null>;

export interface VisitSummaryDto {
  id: number;
  zone: number;
  is_draft: boolean;
  author: VisitAuthorDto;
  started_at: string;
  visit_time: string;
  submitted_at: string | null;
  comment: string;
  groups: VisitGroupScoresDto;
  total: number | null;
  can_edit: boolean;
}

export interface VisitParameterAnswerDto {
  id: number;
  name: string;
  order: number;
  weight_yes: number;
  weight_no: number;
  value: boolean | null;
  comment: string;
  /** true when this answer differs from the same parameter's value on the
   *  most recent prior submitted visit in this zone. */
  changed: boolean;
  /** oldest -> newest, up to 5 prior + current; null = unanswered at that
   *  visit. Only populated (non-empty) for changed parameters. */
  history: (boolean | null)[];
}

export interface VisitGroupDetailDto {
  id: number;
  name: string;
  order: number;
  formula_coefficient: number;
  percentage: number | null;
  previous_percentage: number | null;
  delta: number | null;
  parameters: VisitParameterAnswerDto[];
}

export interface VisitDetailDto {
  id: number;
  zone: number;
  template_id: number;
  is_draft: boolean;
  author: VisitAuthorDto;
  started_at: string;
  visit_time: string;
  submitted_at: string | null;
  comment: string;
  can_edit: boolean;
  can_delete: boolean;
  groups: VisitGroupDetailDto[];
  total: number | null;
  previous_total: number | null;
  total_delta: number | null;
}

/** Shape of the 409 body startVisit() throws when the zone has more than
 *  one template attached and templateId wasn't specified. */
export interface VisitStartAmbiguousDto {
  detail: string;
  templates: { id: number; name: string }[];
}

export const listZoneVisits = (zoneId: string | number, params?: { from?: string; to?: string }) =>
  axiosInstance.get<{ results: VisitSummaryDto[] }>(`/visits/zones/${zoneId}/visits/`, { params });

/** If the zone has more than one template attached and templateId is
 *  omitted, the BE responds 409 with {templates: [{id, name}, ...]} so the
 *  caller can prompt the user to pick one and retry with templateId set. */
export const startVisit = (zoneId: string | number, templateId?: number) =>
  axiosInstance.post<VisitDetailDto>(
    `/visits/zones/${zoneId}/visits/start/`,
    templateId != null ? { template_id: templateId } : undefined,
  );

export const getVisit = (visitId: number) => axiosInstance.get<VisitDetailDto>(`/visits/visits/${visitId}/`);

/** Delete a visit, draft or submitted. Author or superuser only. */
export const deleteVisit = (visitId: number) => axiosInstance.delete(`/visits/visits/${visitId}/`);

export const patchVisit = (visitId: number, data: Partial<{ comment: string; visit_time: string }>) =>
  axiosInstance.patch<VisitDetailDto>(`/visits/visits/${visitId}/`, data);

export const setVisitAnswer = (
  visitId: number,
  data: { parameter_id: number; value: boolean | null; comment?: string },
) => axiosInstance.post<VisitDetailDto>(`/visits/visits/${visitId}/answers/`, data);

export const submitVisit = (visitId: number) =>
  axiosInstance.post<VisitDetailDto>(`/visits/visits/${visitId}/submit/`);

/** "Отправить команде" (Alexey 2026-08-27): render the visit as an image and
 *  post it to the zone's visits chat. Only submitted visits can be sent. */
export const sendVisit = (visitId: number) =>
  axiosInstance.post<{ sent: true }>(`/visits/visits/${visitId}/send/`);

/** Render-only preview of what sendVisit() would post — backs the confirm
 *  modal (Alexey 2026-08-27: "должна вылезать модалка с превью фото"). */
export const getVisitSendPreview = (visitId: number) =>
  axiosInstance.get<Blob>(`/visits/visits/${visitId}/send/preview/`, { responseType: 'blob' });
